import { Injectable } from '@angular/core';

import * as _ from 'lodash';
import * as POSUtils from './../@utils/pos.utils'

import * as sdk from '@mycure/sdk';
import { MycureSDK } from './../service';

import { Logger } from './../logger/logger.service';
const log = new Logger('@mycure/mc-services');

@Injectable({
  providedIn: 'root'
})

export class McBillingService {

  constructor(
    private sdk: MycureSDK,
  ) { 

  }

  loadBillingItemById(id: string): Promise<any> {
    return this.sdk.billing.items()
      .findOne({
        id,
        $populate: {
          _service: { service: 'mf-services', key: 'ref' },
          _variant: { service: 'wh-variants', key: 'ref' },
          _payments: {
            service: 'billing-payments',
            key: 'id',
            method: 'find',
            idField: 'item'
          },
        }
      })
      .then(item => ({ 
        ..._.omit(item, ['$populated']), 
        _service: item.$populated._service, 
        _variant: item.$populated._variant,
        _payments: item.$populated._payments,
      }))
      .then(item => this.loadBillingItemCoverages(item))
      .then(item => this.loadBillingItemServiceCoverages(item))
      .then(item => POSUtils.populateBillingItemComputations(item))
  }

  private loadBillingItemCoverages(billingItem: any): Promise<any[]> {
    return billingItem.coverages ? this.sdk.insurance.coverages()
      .find({
        id: { $in: billingItem.coverages.map((item: any) => item.id) },
        $populate: {
          _orgDetails: { service: 'organizations', key: 'insurer' },
          _contractDetails: { service: 'insurance-contracts', key: 'insurer' },
        }
      })
      .then(res => res.items)
      .then(coverages => coverages.map(item => ({ ..._.omit(item, ['$populated']), _orgDetails: item.$populated._orgDetails, _contractDetails: item.$populated._contractDetails })))
      .then(coverages => coverages.map(coverage => {
        const _insurerName = coverage._orgDetails && coverage._orgDetails.name ? coverage._orgDetails.name :
          coverage._contractDetails && coverage._contractDetails.insurerName ? coverage._contractDetails.insurerName : '';
        return { ..._.omit(coverage, ['_orgDetails', '_contractDetails']), _insurerName };
      }))
      .then((coverages: any[]) => coverages.map(coverage => _.omit(coverage, ['amount', 'type', 'percentage'])))
      .then((coverages: any[]) => {
        billingItem.coverages = billingItem.coverages.map((billingCoverage: any) => {
          let index = coverages.findIndex(coverage => billingCoverage.id === coverage.id);
          if (index > -1)
            billingCoverage = { ...billingCoverage, ...coverages[index], id: billingCoverage.id };
          return billingCoverage;
        });
        return billingItem;
      })
      .then(billingItem => _.omit(billingItem, ['_coverages'])) : Promise.resolve(billingItem);
  }

  private loadBillingItemServiceCoverages(billingItem: any): Promise<any[]> {
    if (billingItem._service && billingItem._service.coverages) {
      return this.sdk.insurance.coverages()
        .find({
          id: { $in: billingItem._service.coverages },
          $populate: {
            _orgDetails: { service: 'organizations', key: 'insurer' },
            _contractDetails: { service: 'insurance-contracts', key: 'insurer' },
          }
        })
        .then(items => items.items)
        .then(coverages => coverages.map(item => ({ ..._.omit(item, ['$populated']), _orgDetails: item.$populated._orgDetails, _contractDetails: item.$populated._contractDetails })))
        .then(res => res.map(coverage => {
          const organizationName = coverage._orgDetails && coverage._orgDetails.name ? coverage._orgDetails.name : undefined;
          const insurerName = coverage._contractDetails && coverage._contractDetails.insurerName ? coverage._contractDetails.insurerName : '';
          return {
            ..._.omit(coverage, ['_orgDetails', '_contractDetails']),
            _details: organizationName ? { name: organizationName } : undefined,
            insurerName,
          };
        }))
        .then(coverages => {
          billingItem._service.coverages = coverages;
          return billingItem;
        });
    } else {
      return Promise.resolve(billingItem);
    }
  }


  /**
   * loadInvoiceById
   * @param organization 
   * @param invoiceId 
   */
  loadInvoiceById(invoiceId: string, organization: string): Promise<any> {
    return this.sdk.billing.invoices()
      .findOne({
        id: invoiceId,
        $populate: {
          _encounter: { service: 'mf-encounters', key: 'id', $populate: { _patient: { service: 'personal-details', key: 'patient' } } },
          _transaction: { service: 'wh-transactions', key: 'id', $populate: { _patient: { service: 'personal-details', key: 'customer' } } },
        }
      })
      .then((invoice: any) => invoice ? this.sdk.billing.items()
        .find({
          invoice: invoiceId, facility: organization,
          $populate: {
            _service: { service: 'mf-services', key: 'ref' },
            _variant: { service: 'wh-variants', key: 'ref' },
            _payments: {
              service: 'billing-payments',
              key: 'id',
              method: 'find',
              idField: 'item'
            },
          }
        })
        .then(res => res.items)
        .then(billingItems => billingItems.map(item => ({ ..._.omit(item, ['$populated']), 
          _service: item.$populated._service, 
          _variant: item.$populated._variant,
          _payments: item.$populated._payments,
        })))
        .then(billingItems => this.loadBillingItemsCoverages(billingItems))
        .then(billingItems => this.loadBillingItemsServiceCoverages(billingItems))
        // .then(billingItems => this.loadBillingItemsPaymentsPromise(billingItems))
        .then(res => POSUtils.populateBillingItemsComputations(res))
        .then(billingItems => ({ ...invoice, _billingItems: billingItems })) : Promise.resolve(invoice))
      .then(invoice => {
        if (invoice) {
          invoice._encounter = { ...invoice.$populated._encounter, 
            _patient: invoice.$populated._encounter ? invoice.$populated._encounter.$populated._patient : {} 
          };
          invoice._transaction = { ...invoice.$populated._transaction, 
            _patient: invoice.$populated._transaction ? invoice.$populated._transaction.$populated._patient : {} 
          };
          return { ..._.omit(invoice, ['$populated']) }
        }
        return undefined;
      })
  }

  private loadBillingItemsCoverages(billingItems: any[]): Promise<any[]> {
    billingItems = billingItems ? billingItems : [];
    return Promise.all(billingItems.map(billingItem => billingItem.coverages ?
      this.sdk.insurance.coverages()
        .find({
          id: { $in: billingItem.coverages.map((item: any) => item.id) },
          $populate: {
            _orgDetails: { service: 'organizations', key: 'insurer' },
            _contractDetails: { service: 'insurance-contracts', key: 'insurer' },
          }
        })
        .then(res => res.items)
        .then(coverages => coverages.map(item => ({ ..._.omit(item, ['$populated']), _orgDetails: item.$populated._orgDetails, _contractDetails: item.$populated._contractDetails })))
        .then(coverages => coverages.map(coverage => {
          const _insurerName = coverage._orgDetails && coverage._orgDetails.name ? coverage._orgDetails.name :
            coverage._contractDetails && coverage._contractDetails.insurerName ? coverage._contractDetails.insurerName : '';
          return { ..._.omit(coverage, ['_orgDetails', '_contractDetails']), _insurerName };
        }))
        .then((coverages: any[]) => coverages.map(coverage => _.omit(coverage, ['amount', 'type', 'percentage'])))
        .then((coverages: any[]) => {
          billingItem.coverages = billingItem.coverages.map((billingCoverage: any) => {
            let index = coverages.findIndex(coverage => billingCoverage.id === coverage.id);
            if (index > -1)
              billingCoverage = { ...billingCoverage, ...coverages[index], id: billingCoverage.id };
            return billingCoverage;
          });
          return billingItem;
        })
        .then(billingItem => _.omit(billingItem, ['_coverages'])) :
      Promise.resolve(billingItem)))
  }

  private loadBillingItemsServiceCoverages(billingItems: any[]): Promise<any[]> {
    return Promise.all(billingItems.map(item => {
      if (item._service && item._service.coverages) {
        return this.sdk.insurance.coverages()
          .find({
            id: { $in: item._service.coverages },
            $populate: {
              _orgDetails: { service: 'organizations', key: 'insurer' },
              _contractDetails: { service: 'insurance-contracts', key: 'insurer' },
            }
          })
          .then(items => items.items)
          .then(coverages => coverages.map(item => ({ ..._.omit(item, ['$populated']), _orgDetails: item.$populated._orgDetails, _contractDetails: item.$populated._contractDetails })))
          .then(res => res.map(coverage => {
            const organizationName = coverage._orgDetails && coverage._orgDetails.name ? coverage._orgDetails.name : undefined;
            const insurerName = coverage._contractDetails && coverage._contractDetails.insurerName ? coverage._contractDetails.insurerName : '';
            return {
              ..._.omit(coverage, ['_orgDetails', '_contractDetails']),
              _details: organizationName ? { name: organizationName } : undefined,
              insurerName,
            };
          }))
          .then(coverages => {
            item._service.coverages = coverages;
            return item;
          });
      } else {
        return Promise.resolve(item);
      }
    }));
  }

  private loadBillingItemsPaymentsPromise(billingItems: any[]): Promise<any[]> {
    const ids: any[] = billingItems.map(val => val.id);
    const query = { item: { $in: ids } };
    return this.sdk.billing.payments()
      .find(query)
      .then(res => res.items)
      .then(payments => billingItems.map((billingItem: any) => {
        billingItem._payments = payments.filter(payment => payment.item === billingItem.id);
        return billingItem
      }));
  }





  updateInvoiceById(organization: string, invoiceId: string, oldData: any, newData: any, billingItems: any[], isFinalized?: boolean): Promise<any> {
    if (isFinalized) {
      return this.updateAllBillingItemsPromise(oldData._billingItems ? oldData._billingItems : [], billingItems, isFinalized);
    } else {
      return this.sdk.billing.invoices()
        .update(invoiceId, _.pick(newData, ['taxComplication', 'notes']))
        .then(res => this.updateAllBillingItemsPromise(oldData._billingItems ? oldData._billingItems : [], billingItems, isFinalized).then(result => res));
    }
  }

  updateAllBillingItemsPromise(oldBillingItems: any[], billingItems: any[], isFinalized?: boolean): Promise<any> {
    let removedBillingItems = oldBillingItems.filter(oldItem => billingItems.findIndex(newItem => oldItem.id === newItem.id) === -1);
    let addedBillingItems = billingItems.filter(newItem => !newItem.id || oldBillingItems.findIndex(oldItem => newItem.id === oldItem.id) === -1);
    let updatedBillingItems = billingItems.filter(newItem => oldBillingItems.findIndex(oldItem => newItem.id === oldItem.id) > -1);

    if (isFinalized) {
      return this.updateAllBillingPayments(oldBillingItems, updatedBillingItems)
    } else {
      return this.removeBillingItemsPromise(removedBillingItems)
        .then(res => this.addBillingItemsPromise(addedBillingItems))
        .then(res => this.updateBillingItemsPromise(oldBillingItems, updatedBillingItems))
        .then(res => this.updateAllBillingPayments(oldBillingItems, updatedBillingItems));
    }
  }

  private removeBillingItemsPromise(removedBillingItems: any[]): Promise<any> {
    return removedBillingItems && removedBillingItems.length > 0 ?
      this.sdk.billing.items().remove({ id: { $in: removedBillingItems.map(item => item.id) } }) :
      Promise.resolve([]);
  }

  private addBillingItemsPromise(addedBillingItems: any[]): Promise<any> {
    return addedBillingItems && addedBillingItems.length > 0 ?
      this.sdk.billing.items().create(addedBillingItems.map(val => _.pick(val, ['parent', 'invoice', 'ref', 'refType', 'refSubtype',
        'providers', 'name', 'description', 'quantity', 'priceCurrency', 'price', 'commissions', 'coverages', 'discounts',
        'discountsNext', 'tax', 'taxCode']))) :
      Promise.resolve([]);
  }

  private updateBillingItemsPromise(oldBillingItems: any[], updatedBillingItems: any[]): Promise<any> {
    if (updatedBillingItems && updatedBillingItems.length > 0) {
      return Promise.all(updatedBillingItems.map(item =>
        this.sdk.billing.items()
          .update(item.id, _.pick(item, ['ref', 'refType', 'refSubtype', 'name', 'description', 'quantity', 'priceCurrency', 
            'price', 'tax', 'taxCode']))
          .then(res => this.updateBillingItemCoverages(oldBillingItems, item).then(result => res))
          .then(res => this.updateBillingItemDiscounts(oldBillingItems, item).then(result => res))
          .then(res => this.updateBillingItemCommissions(oldBillingItems, item).then(result => res)) ))
    } else {
      return Promise.resolve([]);
    }
  }

  private updateBillingItemCoverages(oldBillingItems: any[], billingItem: any): Promise<any> {
    let oldIndex = oldBillingItems.findIndex(old => old.id === billingItem.id);

    if (oldIndex > -1 && oldBillingItems[oldIndex] && oldBillingItems[oldIndex].coverages && oldBillingItems[oldIndex].coverages.length > 0) {
      return this.sdk.billing.items()
        .update(billingItem.id, { $pull: { coverages: { id: { $in: oldBillingItems[oldIndex].coverages.map((coverage: any) => coverage.id) } } } })
        .then(result => billingItem.coverages && billingItem.coverages.length > 0 ?
          this.sdk.billing.items().update(billingItem.id, { $addToSet: { coverages: { $each: billingItem.coverages } } }) : 
          Promise.resolve(result));
    }
    return billingItem.coverages && billingItem.coverages.length > 0 ?
      this.sdk.billing.items().update(billingItem.id, { $addToSet: { coverages: { $each: billingItem.coverages } } }) : Promise.resolve();
  }

  private updateBillingItemDiscounts(oldBillingItems: any[], billingItem: any): Promise<any> {
    let oldIndex = oldBillingItems.findIndex(old => old.id === billingItem.id);
    if (oldIndex > -1 && oldBillingItems[oldIndex] && oldBillingItems[oldIndex].discounts && oldBillingItems[oldIndex].discounts.length > 0) {
      return this.sdk.billing.items()
        .update(billingItem.id, { $pull: { discounts: { id: { $in: oldBillingItems[oldIndex].discounts.map((coverage: any) => coverage.id) } } } })
        .then(result => billingItem.discounts && billingItem.discounts.length > 0 ?
          this.sdk.billing.items().update(billingItem.id, { $addToSet: { discounts: { $each: billingItem.discounts } } }) : 
          Promise.resolve(result));
    }
    return billingItem.discounts && billingItem.discounts.length > 0 ?
      this.sdk.billing.items().update(billingItem.id, { $addToSet: { discounts: { $each: billingItem.discounts } } }) : Promise.resolve();
  }

  private updateBillingItemCommissions(oldBillingItems: any[], billingItem: any): Promise<any> {
    let oldIndex = oldBillingItems.findIndex(old => old.id === billingItem.id);

    if (oldIndex > -1 && oldBillingItems[oldIndex] && oldBillingItems[oldIndex].commissions && oldBillingItems[oldIndex].commissions.length > 0) {
      return this.sdk.billing.items()
        .update(billingItem.id, { $pull: { commissions: { id: { $in: oldBillingItems[oldIndex].commissions.map((commission: any) => commission.id) } } } })
        .then(result => billingItem.commissions && billingItem.commissions.length > 0 ?
          this.sdk.billing.items().update(billingItem.id, { $addToSet: { commissions: { $each: billingItem.commissions } } }) : Promise.resolve(result));
    }
    return billingItem.commissions && billingItem.commissions.length > 0 ?
      this.sdk.billing.items().update(billingItem.id, { $addToSet: { commissions: { $each: billingItem.commissions } } }) : Promise.resolve();
  }

  private updateAllBillingPayments(oldBillingItems: any[], updatedBillingItems: any[]): Promise<any> {
    let allRemovedPayments: any[] = [];
    let allAddedPayments: any[] = [];
    let allUpdatedPayments: any[] = [];

    for (let updateItem of updatedBillingItems) {
      let index = oldBillingItems.findIndex(old => old.id === updateItem.id);
      if (index > -1) {
        const newPayments: any[] = updateItem.payments ? updateItem.payments : [];
        const oldPayments: any[] = oldBillingItems[index]._payments ? oldBillingItems[index]._payments : [];
        const removedPayments: any[] = oldPayments.filter(oldItem => newPayments.findIndex(newItem => oldItem.id === newItem.id) === -1);
        const addedPayments: any[] = newPayments.filter(newItem => !newItem.id || oldPayments.findIndex(oldItem => newItem.id === oldItem.id) === -1);
        const updatedPayments: any[] = newPayments.filter(newItem => oldPayments.findIndex(oldItem => newItem.id === oldItem.id) > -1);
        allRemovedPayments = removedPayments ? allRemovedPayments.concat(removedPayments) : allRemovedPayments;
        allAddedPayments = addedPayments ? allAddedPayments.concat(addedPayments) : allAddedPayments;
        allUpdatedPayments = updatedPayments ? allUpdatedPayments.concat(updatedPayments) : allUpdatedPayments;
      }
    }
  
    return this.removePaymentItemsPromise(allRemovedPayments)
      .then(res => this.addPaymentItemsPromise(allAddedPayments))
      // .then(res => this.updatePaymentItemsPromise(allUpdatedPayments));
  }

  private removePaymentItemsPromise(removePaymentItems: any[]): Promise<any> {
    return removePaymentItems && removePaymentItems.length > 0 ?
      this.sdk.billing.payments().remove({ id: { $in: removePaymentItems.map(item => item.id) } }) :
      Promise.resolve([]);
  }

  private addPaymentItemsPromise(addPaymentItems: any[]): Promise<any> {
    return addPaymentItems && addPaymentItems.length > 0 ?
      this.sdk.billing.payments().create(addPaymentItems) :
      Promise.resolve([]);
  }

  private updatePaymentItemsPromise(updatePaymentItems: any[]): Promise<any> {
    return updatePaymentItems && updatePaymentItems.length > 0 ?
      Promise.all(updatePaymentItems.map(item => this.sdk.billing.payments().update(item.id, _.omit(item, ['id', 'facility', 'createdAt'])))) :
      Promise.resolve([]);
  }




















  private billingItemPaymentMethod(invoice: any, billingItem: any): string {
    let paymentMethods: string[] = [];
    if (POSUtils.computeBillItemResolvedPrice(billingItem, invoice.taxComplication === 'exclusive') > 0) 
      paymentMethods.push('PRIVATE');

    if (billingItem.coverages && billingItem.coverages.length > 0) {
      for (let coverage of billingItem.coverages) {
        if (POSUtils.getBillingItemCoverageByContractIdPrice(billingItem, coverage.contract, invoice.taxComplication === 'exclusive') > 0) {
          let type = coverage.insurerSubtype === 'hmo' ? 'HMO' : coverage.insurerSubtype === 'government' ? 'GOVT' : 'CORP';
          paymentMethods.push(`${type} - ${coverage._insurerName}`);
        }
      }
    }

    return paymentMethods.join(', ');
  }

  private billingItemPaymentMethodDesc(invoice: any, billingItem: any): any[] {
    let paymentMethods: any[] = [];
    if (POSUtils.computeBillItemResolvedPrice(billingItem, invoice.taxComplication === 'exclusive') > 0) 
      paymentMethods.push({ type: 'PRIVATE', name: 'PRIVATE' });

    if (billingItem.coverages && billingItem.coverages.length > 0) {
      for (let coverage of billingItem.coverages) {
        if (POSUtils.getBillingItemCoverageByContractIdPrice(billingItem, coverage.contract, invoice.taxComplication === 'exclusive') > 0) {
          let type = coverage.insurerSubtype === 'hmo' ? 'HMO' : coverage.insurerSubtype === 'government' ? 'GOVT' : 'CORP';
          paymentMethods.push({ type: type, name: coverage._insurerName });
        }
      }
    }

    return paymentMethods;
  }



  /**
   * Load finished invoices transactions by date range
   */
  loadDailyTransactionsByDateRange(facility: string, start: Date, end: Date) {
    return this.sdk.organizations()
      .get(facility)
      .then(org => this.sdk.billing.invoices()
        .find({
          facility,
          finalizedAt: { $gte: start, $lte: end },
          $dateFields: ['finalizedAt.$gte', 'finalizedAt.$lte'],
          $populate: {
            _encounter: { service: 'mf-encounters', key: 'id', $populate: { 
              _patient: { service: 'mf-patients', key: 'patient' } },
              _diagnosis: { service: 'med-records', method: 'find', foreignKey: 'encounter', localKey: 'id', type: 'assessment', subtype: 'diagnosis' }
            },
            _transaction: { service: 'wh-transactions', key: 'id', $populate: { _patient: { service: 'mf-patients', key: 'customer' } } },
            _billingItems: {
              service: 'billing-items', key: 'id', method: 'find', idField: 'invoice',
              $populate: {
                _service: { service: 'mf-services', key: 'ref' },
                _variant: { service: 'wh-variants', key: 'ref' },
                _payments: { service: 'billing-payments', key: 'id', method: 'find', idField: 'item' },
                _coverages: { service: 'insurance-contracts', key: 'coverages', extractKey: 'contract'},
                _commissions: { service: 'org-members', key: 'commissions', extractKey: 'provider', idField: 'uid'},
              }
            }
          }
        })
        .then(res => res.items )
        .then(res => res.map(invoice => {
          invoice._encounter = invoice.$populated._encounter ? { 
            ..._.omit(invoice.$populated._encounter, ['$populated']), 
            _patient: invoice.$populated._encounter.$populated._patient,
            _diagnosis: invoice.$populated._encounter.$populated._diagnosis
          } : { _patient: { } };
          invoice._transaction = invoice.$populated._transaction ? { 
            ..._.omit(invoice.$populated._transaction, ['$populated']), _patient: invoice.$populated._transaction.$populated._patient 
          } : { _patient: { } };
          invoice._billingItems = invoice.$populated._billingItems.map((billingItem: any) => {
            billingItem._service = billingItem.$populated._service;
            billingItem._variant = billingItem.$populated._variant;
            billingItem._payments = billingItem.$populated._payments;
            billingItem._coverages = billingItem.$populated._coverages;
            billingItem._commissions = billingItem.$populated._commissions;
            return { ..._.omit(billingItem, ['$populated']) };
          })
          return { ..._.omit(invoice, ['$populated']) };
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((item: any) => ({
            ...item, 
            coverages: item.coverages.map((coverage: any) => {
              let index = item._coverages.findIndex((_coverage: any) => coverage.contract === _coverage.id);
              if (index > -1) coverage._insurerName = item._coverages[index].insurerName;
              return coverage;
            }), 
            commissions: item.commissions.map((commission: any) => {
              let index = item._commissions.findIndex((_commission: any) => commission.provider === _commission.id || commission.provider === _commission.uid);
              if (index > -1) commission._providerName = item._commissions[index].name;
              return commission;
            }),
            _payments: item._payments ? item._payments.map((payment: any) => {
              if (payment.paymentMethod) {
                let index = org && org.bl_paymentMethods ? org.bl_paymentMethods.findIndex((val: any) => val.code === payment.paymentMethod) : -1;
                if (index > -1)
                  payment._paymentMethod = org.bl_paymentMethods[index];
                else if (payment.paymentMethod === 'CSH')
                  payment._paymentMethod = { code: 'CSH', name: 'Cash', type: 'cash' };
              }
              return payment;
            }) : []
          }));
          return invoice;
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((billingItem: any) => ({ 
              ...billingItem, 
              paymentMethods: this.billingItemPaymentMethod(invoice, billingItem), 
              paymentMethodDesc: this.billingItemPaymentMethodDesc(invoice, billingItem) 
            }))
          return invoice;
        }))
        .then(res => { log.debug('loadDailyTransactionsByDateRange', res); return res }))
  }

  

  loadCompaniesTransactions(facility: string, start: Date, end: Date, compId: string) {
    return this.sdk.organizations()
      .get(facility)
      .then(org => this.sdk.billing.invoices()
      .find({
        facility,
        finalizedAt: { $gte: start, $lte: end },
        $dateFields: ['finalizedAt.$gte', 'finalizedAt.$lte'],
        $prequery: [{
          service: 'billing-items',
          query: {
            facility,
            'coverages.insurerType': 'corporate-partner',
            'coverages.contract': compId ? compId : { $exists: true }
          },
          extractKey: 'invoice',
          resKey: 'id',
          resOps: '$in'
        }
        ],
        $populate: {
          _encounter: {
            service: 'mf-encounters', key: 'id',
            $populate: { 
              _patient: { service: 'mf-patients', key: 'patient' } ,
              _diagnosis: { service: 'med-records', method: 'find', foreignKey: 'encounter', localKey: 'id', type: 'assessment', subtype: 'diagnosis' }
            }
          },
          _transaction: { service: 'wh-transactions', key: 'id', $populate: { _patient: { service: 'mf-patients', key: 'customer' } } },
          _billingItems: {
            service: 'billing-items',
            key: 'id',
            method: 'find',
            idField: 'invoice',
            $prequery: compId ? [{
              service: 'billing-items', extractKey: 'id', resKey: 'id', resOps: '$in',
              query: {
                facility, 'coverages.insurerType': 'corporate-partner',
                'coverages.contract': compId ? compId : { $exists: true }
              }
            }] : [{
              service: 'billing-items', extractKey: 'id', resKey: 'id', resOps: '$in',
              query: { facility, 'coverages.insurerType': 'corporate-partner' }
            }],
            $populate: {
              _service: { service: 'mf-services', key: 'ref' },
              _variant: { service: 'wh-variants', key: 'ref' },
              _payments: { service: 'billing-payments', key: 'id', method: 'find', idField: 'item' },
              _coverages: { service: 'insurance-contracts', key: 'coverages', extractKey: 'contract'},
              _commissions: { service: 'org-members', key: 'commissions', extractKey: 'provider', idField: 'uid'},
            }
          }
        }
      })
      .then(res => res.items)
      .then(res => res.map(invoice => {
        invoice._encounter = invoice.$populated._encounter ? {
          ..._.omit(invoice.$populated._encounter, ['$populated']),
          _patient: invoice.$populated._encounter.$populated._patient,
          _diagnosis: invoice.$populated._encounter.$populated._diagnosis
        } : { _patient: {} };
        invoice._transaction = invoice.$populated._transaction ? {
          ..._.omit(invoice.$populated._transaction, ['$populated']),
          _patient: invoice.$populated._transaction.$populated._patient
        } : { _patient: {} };
        invoice._billingItems = invoice.$populated._billingItems.map((billingItem: any) => {
          billingItem._service = billingItem.$populated._service;
          billingItem._variant = billingItem.$populated._variant;
          billingItem._payments = billingItem.$populated._payments;
          billingItem._coverages = billingItem.$populated._coverages;
          billingItem._commissions = billingItem.$populated._commissions;
          return { ..._.omit(billingItem, ['$populated']) };
        })
        return { ..._.omit(invoice, ['$populated']) };
      }))
      .then(res => res.map(invoice => {
        invoice._billingItems = invoice._billingItems.map((item: any) => {
          return {
            ...item,
            coverages: item.coverages.map((coverage: any) => {
              let index = item._coverages.findIndex((_coverage: any) => coverage.contract === _coverage.id);
              if (index > -1)
                coverage._insurerName = item._coverages[index].insurerName;
              return coverage;
            }),
            commissions: item.commissions.map((commission: any) => {
              let index = item._commissions.findIndex((_commission: any) => commission.provider === _commission.id || commission.provider === _commission.uid);
              if (index > -1) commission._providerName = item._commissions[index].name;
              return commission;
            }),
            _payments: item._payments ? item._payments.map((payment: any) => {
              if (payment.paymentMethod) {
                let index = org && org.bl_paymentMethods ? org.bl_paymentMethods.findIndex((val: any) => val.code === payment.paymentMethod) : -1;
                if (index > -1)
                  payment._paymentMethod = org.bl_paymentMethods[index];
                else if (payment.paymentMethod === 'CSH')
                  payment._paymentMethod = { code: 'CSH', name: 'Cash', type: 'cash' };
              }
              return payment;
            }) : []
          }
        });
        return invoice;
      }))
      .then(res => res.map(invoice => {
        invoice._billingItems = invoice._billingItems.map((billingItem: any) => {
          return {
            ...billingItem,
            paymentMethods: this.billingItemPaymentMethod(invoice, billingItem),
            paymentMethodDesc: this.billingItemPaymentMethodDesc(invoice, billingItem)
          };
        })
        return invoice;
      }))
      .then(res => { log.debug('loadCompaniesTransactions', res); return res }))
  }

  loadDoctorsTransactions(facility: string, start: Date, end: Date, doctorUid: string) {
    return this.sdk.organizations()
      .get(facility)
      .then(org => this.sdk.billing.invoices()
        .find({
          facility,
          finalizedAt: { $gte: start, $lte: end },
          $dateFields: ['finalizedAt.$gte', 'finalizedAt.$lte'],
          type: 'mf-encounter',
          $prequeryOr: [{
            service: 'mf-encounters', extractKey: 'id', resKey: 'id', resOps: '$in',
            query: { 
              facility, $or: [
                { doctor: doctorUid ? doctorUid : { $exists: true } },
                { doctors: doctorUid ? doctorUid : { $exists: true, $not: { $size: 0 } } },
                { createdBy: doctorUid ? doctorUid : { $exists: true } }
              ]
            }
          }, {
            service: 'billing-items', extractKey: 'invoice', resKey: 'id', resOps: '$in',
            query: {
              facility, $or: [
                { 'commissions.provider': doctorUid ? doctorUid : { $exists: true } },
                { providers: doctorUid ? doctorUid : { $exists: true, $not: { $size: 0 } } },
                { createdBy: doctorUid ? doctorUid : { $exists: true } }
              ]
            }
          }, {
            service: 'med-records', extractKey: 'encounter', resKey: 'id', resOps: '$in',
            query: {
              facility, $or: [
                { providers: doctorUid ? doctorUid : { $exists: true, $not: { $size: 0 } } },
                { createdBy: doctorUid ? doctorUid : { $exists: true } }
              ]
            }
          }],
          $populate: {
            _encounter: {
              service: 'mf-encounters', key: 'id',
              $populate: { 
                _patient: { service: 'mf-patients', key: 'patient' },
                _diagnosis: { service: 'med-records', method: 'find', foreignKey: 'encounter', localKey: 'id', type: 'assessment', subtype: 'diagnosis' }
              }
            },
            _transaction: { service: 'wh-transactions', key: 'id', $populate: { _patient: { service: 'mf-patients', key: 'customer' } } },
            _billingItems: {
              service: 'billing-items',
              key: 'id',
              method: 'find',
              idField: 'invoice',
              $populate: {
                _service: { service: 'mf-services', key: 'ref' },
                _variant: { service: 'wh-variants', key: 'ref' },
                _payments: { service: 'billing-payments', key: 'id', method: 'find', idField: 'item' },
                _coverages: { service: 'insurance-contracts', key: 'coverages', extractKey: 'contract' },
                _commissions: { service: 'org-members', key: 'commissions', extractKey: 'provider', idField: 'uid'},
              }
            }
          }
        })
        .then(res => res.items)
        .then(res => res.map(invoice => {
          invoice._encounter = invoice.$populated._encounter ? {
            ..._.omit(invoice.$populated._encounter, ['$populated']),
            _patient: invoice.$populated._encounter.$populated._patient,
            _diagnosis: invoice.$populated._encounter.$populated._diagnosis
          } : { _patient: {} };
          invoice._transaction = invoice.$populated._transaction ? {
            ..._.omit(invoice.$populated._transaction, ['$populated']),
            _patient: invoice.$populated._transaction.$populated._patient
          } : { _patient: {} };
          invoice._billingItems = invoice.$populated._billingItems.map((billingItem: any) => {
            billingItem._service = billingItem.$populated._service;
            billingItem._variant = billingItem.$populated._variant;
            billingItem._payments = billingItem.$populated._payments;
            billingItem._coverages = billingItem.$populated._coverages;
            billingItem._commissions = billingItem.$populated._commissions;
            return { ..._.omit(billingItem, ['$populated']) };
          })
          return { ..._.omit(invoice, ['$populated']) };
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((item: any) => {
            return {
              ...item,
              coverages: item.coverages.map((coverage: any) => {
                let index = item._coverages.findIndex((_coverage: any) => coverage.contract === _coverage.id);
                if (index > -1)
                  coverage._insurerName = item._coverages[index].insurerName;
                return coverage;
              }),
              commissions: item.commissions.map((commission: any) => {
                let index = item._commissions.findIndex((_commission: any) => commission.provider === _commission.id || commission.provider === _commission.uid);
                if (index > -1) commission._providerName = item._commissions[index].name;
                return commission;
              }),
              _payments: item._payments ? item._payments.map((payment: any) => {
                if (payment.paymentMethod) {
                  let index = org && org.bl_paymentMethods ? org.bl_paymentMethods.findIndex((val: any) => val.code === payment.paymentMethod) : -1;
                  if (index > -1)
                    payment._paymentMethod = org.bl_paymentMethods[index];
                  else if (payment.paymentMethod === 'CSH')
                    payment._paymentMethod = { code: 'CSH', name: 'Cash', type: 'cash' };
                }
                return payment;
              }) : []
            }
          });
          return invoice;
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((billingItem: any) => {
            return {
              ...billingItem,
              paymentMethods: this.billingItemPaymentMethod(invoice, billingItem),
              paymentMethodDesc: this.billingItemPaymentMethodDesc(invoice, billingItem)
            };
          })
          return invoice;
        }))
        .then(res => { log.debug('loadCompaniesTransactions', res); return res }))
  }

  loadGovsTransactions(facility: string, start: Date, end: Date, govId: string) {
    return this.sdk.organizations()
      .get(facility)
      .then(org => this.sdk.billing.invoices()
        .find({
          facility,
          finalizedAt: { $gte: start, $lte: end },
          $dateFields: ['finalizedAt.$gte', 'finalizedAt.$lte'],
          $prequery: [{
            service: 'billing-items',
            query: {
              facility,
              'coverages.insurerType': 'insurance',
              'coverages.insurerSubtype': 'government',
              'coverages.contract': govId ? govId : { $exists: true }
            },
            extractKey: 'invoice',
            resKey: 'id',
            resOps: '$in'
          }],
          $populate: {
            _encounter: {
              service: 'mf-encounters', key: 'id',
              $populate: { 
                _patient: { service: 'mf-patients', key: 'patient' } ,
                _diagnosis: { service: 'med-records', method: 'find', foreignKey: 'encounter', localKey: 'id', type: 'assessment', subtype: 'diagnosis' }
              }
            },
            _transaction: {
              service: 'wh-transactions', key: 'id', $populate: { _patient: { service: 'mf-patients', key: 'customer' } }
            },
            _billingItems: {
              service: 'billing-items', key: 'id', method: 'find', idField: 'invoice',
              $prequery: govId ? [{
                service: 'billing-items', extractKey: 'id', resKey: 'id', resOps: '$in',
                query: {
                  facility, 'coverages.insurerType': 'insurance', 'coverages.insurerSubtype': 'government', 'coverages.contract': govId ? govId : { $exists: true }
                },
              }] : [{
                service: 'billing-items', extractKey: 'id', resKey: 'id', resOps: '$in',
                query: {
                  facility, 'coverages.insurerType': 'insurance', 'coverages.insurerSubtype': 'government',
                }
              }],
              $populate: {
                _service: { service: 'mf-services', key: 'ref' },
                _variant: { service: 'wh-variants', key: 'ref' },
                _payments: { service: 'billing-payments', key: 'id', method: 'find', idField: 'item' },
                _coverages: { service: 'insurance-contracts', key: 'coverages', extractKey: 'contract' },
                _commissions: { service: 'org-members', key: 'commissions', extractKey: 'provider', idField: 'uid'},
              }
            }
          }
        })
        .then(res => res.items)
        .then(res => res.map(invoice => {
          invoice._encounter = invoice.$populated._encounter ? {
            ..._.omit(invoice.$populated._encounter, ['$populated']),
            _patient: invoice.$populated._encounter.$populated._patient,
            _diagnosis: invoice.$populated._encounter.$populated._diagnosis
          } : { _patient: {} };
          invoice._transaction = invoice.$populated._transaction ? {
            ..._.omit(invoice.$populated._transaction, ['$populated']),
            _patient: invoice.$populated._transaction.$populated._patient
          } : { _patient: {} };
          invoice._billingItems = invoice.$populated._billingItems.map((billingItem: any) => {
            billingItem._service = billingItem.$populated._service;
            billingItem._variant = billingItem.$populated._variant;
            billingItem._payments = billingItem.$populated._payments;
            billingItem._coverages = billingItem.$populated._coverages;
            billingItem._commissions = billingItem.$populated._commissions;
            return { ..._.omit(billingItem, ['$populated']) };
          })
          return { ..._.omit(invoice, ['$populated']) };
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((item: any) => {
            return {
              ...item,
              coverages: item.coverages.map((coverage: any) => {
                let index = item._coverages.findIndex((_coverage: any) => coverage.contract === _coverage.id);
                if (index > -1)
                  coverage._insurerName = item._coverages[index].insurerName;
                return coverage;
              }),
              commissions: item.commissions.map((commission: any) => {
                let index = item._commissions.findIndex((_commission: any) => commission.provider === _commission.id || commission.provider === _commission.uid);
                if (index > -1) commission._providerName = item._commissions[index].name;
                return commission;
              }),
              _payments: item._payments ? item._payments.map((payment: any) => {
                if (payment.paymentMethod) {
                  let index = org && org.bl_paymentMethods ? org.bl_paymentMethods.findIndex((val: any) => val.code === payment.paymentMethod) : -1;
                  if (index > -1)
                    payment._paymentMethod = org.bl_paymentMethods[index];
                  else if (payment.paymentMethod === 'CSH')
                    payment._paymentMethod = { code: 'CSH', name: 'Cash', type: 'cash' };
                }
                return payment;
              }) : []
            }
          });
          return invoice;
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((billingItem: any) => {
            return {
              ...billingItem,
              paymentMethods: this.billingItemPaymentMethod(invoice, billingItem),
              paymentMethodDesc: this.billingItemPaymentMethodDesc(invoice, billingItem)
            };
          })
          return invoice;
        }))
        .then(res => { log.debug('loadGovsTransactions', res); return res }))
  }

  loadHMOsTransactions(facility: string, start: Date, end: Date, hmoId: string) {
    return this.sdk.organizations()
      .get(facility)
      .then(org => this.sdk.billing.invoices()
        .find({
          facility,
          finalizedAt: { $gte: start, $lte: end },
          $dateFields: ['finalizedAt.$gte', 'finalizedAt.$lte'],
          $prequery: [{
            service: 'billing-items',
            query: {
              facility,
              'coverages.insurerType': 'insurance',
              'coverages.insurerSubtype': 'hmo',
              'coverages.contract': hmoId ? hmoId : { $exists: true }
            },
            extractKey: 'invoice',
            resKey: 'id',
            resOps: '$in'
          }],
          $populate: {
            _encounter: {
              service: 'mf-encounters',
              key: 'id',
              $populate: { 
                _patient: { service: 'mf-patients', key: 'patient' } ,
                _diagnosis: { service: 'med-records', method: 'find', foreignKey: 'encounter', localKey: 'id', 
                  type: 'assessment', subtype: 'diagnosis' }
              }
            },
            _transaction: {
              service: 'wh-transactions',
              key: 'id',
              $populate: { _patient: { service: 'mf-patients', key: 'customer' } }
            },
            _billingItems: {
              service: 'billing-items',
              key: 'id',
              method: 'find',
              idField: 'invoice',
              $prequery: hmoId ? [{
                service: 'billing-items',
                query: {
                  facility,
                  'coverages.insurerType': 'insurance',
                  'coverages.insurerSubtype': 'hmo',
                  'coverages.contract': hmoId ? hmoId : { $exists: true }
                },
                extractKey: 'id',
                resKey: 'id',
                resOps: '$in'
              }] : [{
                service: 'billing-items',
                query: {
                  facility,
                  'coverages.insurerType': 'insurance',
                  'coverages.insurerSubtype': 'hmo',
                },
                extractKey: 'id',
                resKey: 'id',
                resOps: '$in'
              }],
              $populate: {
                _service: { service: 'mf-services', key: 'ref' },
                _variant: { service: 'wh-variants', key: 'ref' },
                _payments: {
                  service: 'billing-payments',
                  key: 'id',
                  method: 'find',
                  idField: 'item'
                },
                _coverages: {
                  service: 'insurance-contracts',
                  key: 'coverages',
                  extractKey: 'contract'
                },
                _commissions: { service: 'org-members', key: 'commissions', extractKey: 'provider', idField: 'uid'},
              }
            }
          }
        })
        .then(res => res.items)
        .then(res => res.map(invoice => {
          invoice._encounter = invoice.$populated._encounter ? {
            ..._.omit(invoice.$populated._encounter, ['$populated']),
            _patient: invoice.$populated._encounter.$populated._patient,
            _diagnosis: invoice.$populated._encounter.$populated._diagnosis,
          } : { _patient: {} };
          invoice._transaction = invoice.$populated._transaction ? {
            ..._.omit(invoice.$populated._transaction, ['$populated']),
            _patient: invoice.$populated._transaction.$populated._patient
          } : { _patient: {} };
          invoice._billingItems = invoice.$populated._billingItems.map((billingItem: any) => {
            billingItem._service = billingItem.$populated._service;
            billingItem._variant = billingItem.$populated._variant;
            billingItem._payments = billingItem.$populated._payments;
            billingItem._coverages = billingItem.$populated._coverages;
            billingItem._commissions = billingItem.$populated._commissions;
            return { ..._.omit(billingItem, ['$populated']) };
          })
          return { ..._.omit(invoice, ['$populated']) };
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((item: any) => {
            return {
              ...item,
              coverages: item.coverages.map((coverage: any) => {
                let index = item._coverages.findIndex((_coverage: any) => coverage.contract === _coverage.id);
                if (index > -1)
                  coverage._insurerName = item._coverages[index].insurerName;
                return coverage;
              }),
              commissions: item.commissions.map((commission: any) => {
                let index = item._commissions.findIndex((_commission: any) => commission.provider === _commission.id || commission.provider === _commission.uid);
                if (index > -1) commission._providerName = item._commissions[index].name;
                return commission;
              }),
              _payments: item._payments ? item._payments.map((payment: any) => {
                if (payment.paymentMethod) {
                  let index = org && org.bl_paymentMethods ? org.bl_paymentMethods.findIndex((val: any) => val.code === payment.paymentMethod) : -1;
                  if (index > -1)
                    payment._paymentMethod = org.bl_paymentMethods[index];
                  else if (payment.paymentMethod === 'CSH')
                    payment._paymentMethod = { code: 'CSH', name: 'Cash', type: 'cash' };
                }
                return payment;
              }) : []
            }
          });
          return invoice;
        }))
        .then(res => res.map(invoice => {
          invoice._billingItems = invoice._billingItems.map((billingItem: any) => {
            return {
              ...billingItem,
              paymentMethods: this.billingItemPaymentMethod(invoice, billingItem),
              paymentMethodDesc: this.billingItemPaymentMethodDesc(invoice, billingItem)
            };
          })
          return invoice;
        }))
        .then(res => { log.debug('loadHMOsTransactions', res); return res }))
  }
}
